import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Container, Tooltip } from '@mui/material';
import { useAuth } from '../Auth';
import { useSnackbar } from '../context/SnackbarContext';
import api from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircle as CheckCircleIcon,
    ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { validatePassword } from '../utils/validation';

// Reusable requirement item component from UserManagement.js
const RequirementItem = ({ label, met, isFieldEmpty, tooltip = "", tooltipPlacement = "top" }) => {
    const isActive = !isFieldEmpty && met;

    const content = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            {isActive ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
                <ErrorOutlineIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            )}
            <Typography
                variant="caption"
                sx={{
                    color: isActive ? 'success.main' : 'text.secondary',
                    lineHeight: 1
                }}
            >
                {label}
            </Typography>
        </Box>
    );

    return tooltip ? (
        <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
            {content}
        </Tooltip>
    ) : content;
};

export default function ForcePasswordChange() {
    const { user, setUser } = useAuth();
    const showSnackbar = useSnackbar();
    const navigate = useNavigate();
    const [passwords, setPasswords] = useState({ new: '', confirm: '' });
    const passwordSpecs = validatePassword(passwords.new, user?.username || '');
    const isPasswordValid = Object.entries(passwordSpecs)
        .filter(([key]) => key !== 'isEmpty')
        .every(([_, val]) => val);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isPasswordValid) {
            return showSnackbar("Please meet all password requirements", "error");
        }

        if (passwords.new !== passwords.confirm) {
            return showSnackbar("Passwords do not match", "error");
        }

        try {
            await api.post('/auth/update-forced-password', {
                password: passwords.new
            });

            // Update local user state so requiresPasswordChange becomes false
            const updatedUser = { ...user, requiresPasswordChange: false };
            localStorage.setItem('user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            showSnackbar("Password updated successfully!", "success");
            navigate('/'); // Send them to dashboard
        } catch (err) {
            showSnackbar("Failed to update password", "error");
        }
    };

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Paper sx={{ p: 4, width: '100%', textAlign: 'center', borderRadius: 3 }}>

                    {/* Branded Header */}
                    <Box
                        onClick={() => window.location.href = '/'}
                        className="header"
                        sx={{
                            mx: -4,
                            mt: -4,
                            mb: 3,
                            cursor: 'pointer',
                            borderRadius: '8px 8px 0 0',
                            overflow: 'hidden',
                            lineHeight: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src="/Verisafe logo light.png"
                            alt="VeriSafe Logo"
                            sx={{
                                width: '300px',
                                height: 'auto',
                                display: 'block',
                            }}
                        />
                    </Box>

                    <Typography variant="h5" gutterBottom fontWeight="bold">
                        Secure Your Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        This is a temporary password. Please set a new secure password to continue.
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="New Password"
                            type="password"
                            margin="normal"
                            required
                            value={passwords.new}
                            onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                            error={!passwordSpecs.isEmpty && !isPasswordValid}
                        />

                        {/* Password Requirements Display */}
                        <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                            {/* <RequirementItem label="At least 8 characters" met={passwordSpecs.isLongEnough} isFieldEmpty={passwordSpecs.isEmpty} />
                            <RequirementItem label="One uppercase letter" met={passwordSpecs.hasUppercase} isFieldEmpty={passwordSpecs.isEmpty} />
                            <RequirementItem label="One number" met={passwordSpecs.hasNumber} isFieldEmpty={passwordSpecs.isEmpty} />
                            <RequirementItem label="One special character" met={passwordSpecs.hasSpecialChar} isFieldEmpty={passwordSpecs.isEmpty} />
                            <RequirementItem label="Does not contain username" met={passwordSpecs.isNotUsername} isFieldEmpty={passwordSpecs.isEmpty} /> */}

                            <RequirementItem label="Minimum of 8 characters" met={passwordSpecs.minLength} isFieldEmpty={passwordSpecs.isEmpty} />
                            <RequirementItem label="Contains uppercase letters" met={passwordSpecs.hasUpper} isFieldEmpty={passwordSpecs.isEmpty} tooltip="At least one capital letter (A-Z)" tooltipPlacement="right" />
                            <RequirementItem label="Contains numbers" met={passwordSpecs.hasNumber} isFieldEmpty={passwordSpecs.isEmpty} tooltip="At least one digit (0-9)" tooltipPlacement="left" />
                            <RequirementItem label="Contains special characters" met={passwordSpecs.hasSpecial} isFieldEmpty={passwordSpecs.isEmpty} tooltip="e.g. ! @ # $ % ^ & *" tooltipPlacement="right" />
                            <RequirementItem label="Not the same as username" met={passwordSpecs.notUsername} isFieldEmpty={passwordSpecs.isEmpty} tooltip="Password cannot match your username" tooltipPlacement="left" />
                            <RequirementItem label="Exclude common names" met={passwordSpecs.notCommon} isFieldEmpty={passwordSpecs.isEmpty} tooltip="Avoid obvious choices like 'password' or '123456'" tooltipPlacement="right" />

                        </Box>

                        <TextField
                            fullWidth
                            label="Confirm New Password"
                            type="password"
                            margin="normal"
                            required
                            value={passwords.confirm}
                            onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                            error={passwords.confirm !== '' && passwords.new !== passwords.confirm}
                            helperText={
                                (passwords.confirm !== '' && passwords.new !== passwords.confirm)
                                    ? "Passwords do not match"
                                    : ""
                            }
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!isPasswordValid || passwords.new !== passwords.confirm}
                            sx={{ mt: 3, py: 1.5, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            Update & Continue
                        </Button>
                    </form>
                </Paper>
            </Box>
        </Container>
    );
}